/*global payhere*/
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuthContext } from "@asgardeo/auth-react";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import DialogModal from "../../components/Modal/DialogModal";
import MainLayout from "../../layouts/MainLayout";
import ChatStatusBanner from "../../components/ChatPricing/ChatStatusBanner";
import {
  CHAT_DB_SERVER_URL,
  NOTIFY_URL,
  PAYMENTS_SERVER_URL,
} from "../../config/backend-endpoints";
import { ensureUtcIso } from "../../utils/dateTime";

const PRODUCT_FEATURES = [
  {
    n: "01",
    tag: "Research",
    title: "Researcher Silva 2, a deep legal research agent.",
    description:
      "Answers hard legal questions and cites the statutes and case law it relies on.",
  },
  {
    n: "02",
    tag: "Privacy",
    title: "Instant delete, with no server retention.",
    description:
      "Delete any chat or uploaded file at once, with nothing kept on the server. Temporary chats clear themselves within 24 hours.",
  },
  {
    n: "03",
    tag: "Sources",
    title: "Web search built on official Sri Lankan sources.",
    description:
      "Searches gazettes, regulations, and circulars from official government sites, so every answer traces back to the source of record.",
  },
  {
    n: "04",
    tag: "Translation",
    title: "Translator Siriwardena 1 for trilingual legal translation.",
    description:
      "Translates legal documents across Sinhala, Tamil, and English, with terms of art preserved in place.",
  },
  {
    n: "05",
    tag: "Coming soon",
    title: "Drafter and reviewer agents.",
    description:
      "Drafting and review agents alongside research and translation. Arriving soon.",
  },
];

const INCLUDED_ITEMS = [
  "Monthly access to chat.paralegal.lk",
  "Ongoing platform and feature updates",
];

const GROUP_PLAN_HIGHLIGHTS = [
  "Multi-user access for legal teams",
  "Volume discounts that scale with seat count",
  "Coordinated onboarding and support",
];

const STATUS_OPTIONS = new Set([
  "active",
  "pending_activation",
  "activation_failed",
  "inactive",
  "manual_review",
  "trial_active",
  "trial_ending_soon",
  "trial_expired_access_paused",
  "trial_pending_activation",
  "trial_activation_failed",
  // Legacy URL/query compatibility states
  "success",
  "activating",
  "pending",
  "failed",
  "already_subscribed",
]);

const TRIAL_PLAN_CODE = "CHAT_PRO_MONTHLY";
const TRIAL_LIFECYCLE_STATUS_OPTIONS = new Set([
  "trialing",
  "converted_to_paid",
  "expired",
  "access_removal_started",
]);

const LEGACY_STATUS_TO_CANONICAL = {
  success: "active",
  already_subscribed: "active",
  activating: "pending_activation",
  pending: "pending_activation",
  failed: "activation_failed",
};

const normalizeUiStatus = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) {
    return null;
  }
  if (LEGACY_STATUS_TO_CANONICAL[normalized]) {
    return LEGACY_STATUS_TO_CANONICAL[normalized];
  }
  if (STATUS_OPTIONS.has(normalized)) {
    return normalized;
  }
  return null;
};

const normalizeTrialLifecycleStatus = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) {
    return null;
  }
  if (TRIAL_LIFECYCLE_STATUS_OPTIONS.has(normalized)) {
    return normalized;
  }
  return null;
};

const toOptionalIsoDate = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return null;
  }
  const parsed = new Date(ensureUtcIso(normalized));
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toISOString();
};

const calculateTrialDaysRemaining = (trialEndsAtIso) => {
  if (!trialEndsAtIso) {
    return null;
  }
  const trialEndsAtMs = Date.parse(ensureUtcIso(trialEndsAtIso));
  if (Number.isNaN(trialEndsAtMs)) {
    return null;
  }
  const diffMs = trialEndsAtMs - Date.now();
  return Math.max(0, Math.ceil(diffMs / 86400000));
};

const normalizeTrialDaysRemaining = (value, trialEndsAtIso) => {
  if (value !== null && value !== undefined && value !== "") {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return Math.max(0, Math.ceil(numeric));
    }
  }
  return calculateTrialDaysRemaining(trialEndsAtIso);
};

const extractTrialProjection = (payload) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  let trialStatus = normalizeTrialLifecycleStatus(payload?.trial_status);
  if (!trialStatus) {
    trialStatus = normalizeTrialLifecycleStatus(payload?.status);
  }

  const reason = String(payload?.reason || "").trim().toLowerCase();
  if (!trialStatus && reason === "trial_started_pending_activation") {
    trialStatus = "trialing";
  }

  let trialEndsAt = toOptionalIsoDate(payload?.trial_ends_at);
  if (!trialEndsAt && reason.startsWith("trial_")) {
    trialEndsAt = toOptionalIsoDate(payload?.effective_to);
  }

  const accessActionStatus = String(payload?.access_action_status || "").trim().toLowerCase() || null;
  const trialDaysRemaining = normalizeTrialDaysRemaining(payload?.trial_days_remaining, trialEndsAt);
  const backendEligible =
    typeof payload?.trial_eligible === "boolean" ? payload.trial_eligible : null;
  const existing = typeof payload?.existing === "boolean" ? payload.existing : null;

  const hasTrialFields =
    trialStatus !== null ||
    trialEndsAt !== null ||
    trialDaysRemaining !== null ||
    accessActionStatus !== null ||
    backendEligible !== null ||
    existing !== null;

  if (!hasTrialFields) {
    return null;
  }

  return {
    trialStatus,
    trialEndsAt,
    trialDaysRemaining,
    accessActionStatus,
    backendEligible,
    existing,
  };
};

const mergeTrialProjection = (previous, incoming) => {
  if (!incoming) {
    return previous;
  }
  if (!previous) {
    return incoming;
  }

  return {
    trialStatus: incoming.trialStatus || previous.trialStatus || null,
    trialEndsAt: incoming.trialEndsAt || previous.trialEndsAt || null,
    trialDaysRemaining:
      incoming.trialDaysRemaining !== null && incoming.trialDaysRemaining !== undefined
        ? incoming.trialDaysRemaining
        : previous.trialDaysRemaining,
    accessActionStatus: incoming.accessActionStatus || previous.accessActionStatus || null,
    backendEligible:
      incoming.backendEligible !== null && incoming.backendEligible !== undefined
        ? incoming.backendEligible
        : previous.backendEligible,
    existing:
      incoming.existing !== null && incoming.existing !== undefined
        ? incoming.existing
        : previous.existing,
  };
};

const deriveTrialUiStatus = (canonicalStatus, trialProjection) => {
  const trialStatus = normalizeTrialLifecycleStatus(trialProjection?.trialStatus);
  if (!trialStatus || trialStatus === "converted_to_paid") {
    return null;
  }

  if (trialStatus === "expired" || trialStatus === "access_removal_started") {
    return "trial_expired_access_paused";
  }

  const accessActionStatus = String(trialProjection?.accessActionStatus || "")
    .trim()
    .toLowerCase();
  if (
    canonicalStatus === "activation_failed" ||
    accessActionStatus === "pending_activation_enqueue_failed"
  ) {
    return "trial_activation_failed";
  }
  if (
    canonicalStatus === "pending_activation" ||
    accessActionStatus.startsWith("pending_activation")
  ) {
    return "trial_pending_activation";
  }

  if ((trialProjection?.trialDaysRemaining ?? null) !== null && trialProjection.trialDaysRemaining <= 2) {
    return "trial_ending_soon";
  }

  return "trial_active";
};

const resolveUiProjection = (payload, priorTrialProjection = null) => {
  const canonicalStatus = mapBackendProjectionToUiStatus(payload);
  const mergedTrialProjection = mergeTrialProjection(
    priorTrialProjection,
    extractTrialProjection(payload)
  );
  const trialUiStatus = deriveTrialUiStatus(canonicalStatus, mergedTrialProjection);

  return {
    canonicalStatus,
    status: trialUiStatus || canonicalStatus,
    trialProjection: mergedTrialProjection,
  };
};

const describeTrialDaysRemaining = (daysRemaining) => {
  if (typeof daysRemaining !== "number" || !Number.isFinite(daysRemaining)) {
    return "";
  }
  if (daysRemaining <= 0) {
    return "Trial ends today.";
  }
  if (daysRemaining === 1) {
    return "1 day remaining in your trial.";
  }
  return `${daysRemaining} days remaining in your trial.`;
};

const buildStatusNotice = (status, trialProjection, fallbackMessage = "") => {
  const trialDays = describeTrialDaysRemaining(trialProjection?.trialDaysRemaining);

  if (status === "active") {
    return "This account already has active chat access.";
  }
  if (status === "trial_active") {
    return trialDays
      ? `${trialDays} Subscribe now to continue uninterrupted after your trial.`
      : "Your free trial is active. Subscribe now to continue uninterrupted.";
  }
  if (status === "trial_ending_soon") {
    return trialDays
      ? `${trialDays} Subscribe now so access continues after trial.`
      : "Your trial is ending soon. Subscribe now so access continues.";
  }
  if (status === "trial_pending_activation") {
    return "Trial started, access activation is in progress.";
  }
  if (status === "trial_activation_failed") {
    return "Trial started, but automatic activation failed. Please retry shortly or contact support.";
  }
  if (status === "trial_expired_access_paused") {
    return "Trial ended. Subscribe to restore access.";
  }
  if (status === "manual_review") {
    return "Your payment is under manual review. Access will activate after verification.";
  }
  if (status === "pending_activation") {
    return "Payment is verified and access activation is in progress.";
  }
  if (status === "activation_failed") {
    return "We could not activate your access automatically. Please contact support.";
  }
  if (fallbackMessage) {
    return fallbackMessage;
  }
  return "";
};

const mapBackendProjectionToUiStatus = (payload) => {
  const accessStatus = normalizeUiStatus(payload?.access_status);
  const status = normalizeUiStatus(payload?.status);
  const billingStatus = String(payload?.billing_status || "").trim().toLowerCase();

  if (payload?.chat_access === true || accessStatus === "active" || status === "active") {
    return "active";
  }

  if (
    accessStatus === "manual_review" ||
    status === "manual_review" ||
    billingStatus === "manual_review"
  ) {
    return "manual_review";
  }

  if (accessStatus === "activation_failed" || status === "activation_failed") {
    return "activation_failed";
  }

  if (accessStatus === "pending_activation" || status === "pending_activation") {
    return "pending_activation";
  }

  if (
    billingStatus === "pending_payment" ||
    billingStatus === "payment_verified" ||
    billingStatus === "subscription_active"
  ) {
    return "pending_activation";
  }

  if (accessStatus === "inactive" || status === "inactive") {
    return "inactive";
  }

  if (billingStatus === "cancelled" || billingStatus === "expired") {
    return "inactive";
  }

  // Conservative default: never claim active for unknown state.
  return "inactive";
};

const STATUS_POLL_INTERVAL_MS = 3000;
const STATUS_POLL_MAX_ATTEMPTS = 20;

const chatUrl =
  import.meta.env.VITE_CHAT_DOMAIN_URL || "https://chat.paralegal.lk";
const chatDomainLabel = chatUrl.replace(/^https?:\/\//, "").replace(/\/$/, "");
const chatDbServerUrl = CHAT_DB_SERVER_URL;
const paymentsServerUrl = PAYMENTS_SERVER_URL;

const ChatPricing = () => {
  const { state, signIn, getAccessToken, getBasicUserInfo, getDecodedIDToken } =
    useAuthContext();
  const [searchParams] = useSearchParams();
  const [modalState, setModalState] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isStartingTrial, setIsStartingTrial] = useState(false);
  const [isCheckingEntitlement, setIsCheckingEntitlement] = useState(false);
  const [isPollingStatus, setIsPollingStatus] = useState(false);
  const [runtimeStatus, setRuntimeStatus] = useState(null);
  const [trialProjection, setTrialProjection] = useState(null);
  const [activationNotice, setActivationNotice] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const pollingIntervalRef = useRef(null);
  const inFlightPollRef = useRef(false);
  const trialProjectionRef = useRef(null);

  const statusFromQuery = useMemo(() => {
    const value = searchParams.get("status");
    return normalizeUiStatus(value);
  }, [searchParams]);
  const orderIdFromQuery = useMemo(() => searchParams.get("order_id"), [searchParams]);
  const status = runtimeStatus || statusFromQuery;
  const isPaidActiveState = status === "active";
  const canOpenChat = status === "active" || status === "trial_active" || status === "trial_ending_soon";

  useEffect(() => {
    trialProjectionRef.current = trialProjection;
  }, [trialProjection]);

  useEffect(() => {
    if (document.querySelector('script[src*="payhere.lk/lib/payhere.js"]')) {
      return;
    }

    const script = document.createElement("script");
    script.src = "https://www.payhere.lk/lib/payhere.js";
    script.defer = true;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const buildStatusUrl = (nextStatus, orderId = null) => {
    const url = new URL(window.location.href);
    url.searchParams.set("status", nextStatus);
    if (orderId) {
      url.searchParams.set("order_id", orderId);
    }
    return url.toString();
  };

  const updateStatusInUrl = useCallback((nextStatus, orderId = null) => {
    const url = new URL(window.location.href);
    url.searchParams.set("status", nextStatus);
    if (orderId) {
      url.searchParams.set("order_id", orderId);
    } else {
      url.searchParams.delete("order_id");
    }
    window.history.replaceState({}, "", url.toString());
  }, []);

  const stopStatusPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      window.clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    inFlightPollRef.current = false;
    setIsPollingStatus(false);
  }, []);

  const startStatusPolling = useCallback(
    (accessToken, orderId) => {
      if (!orderId || !accessToken) {
        return;
      }

      stopStatusPolling();
      setIsPollingStatus(true);
      setRuntimeStatus("pending_activation");
      setActivationNotice("Payment received. Activating your chat access...");
      updateStatusInUrl("pending_activation", orderId);

      let attempts = 0;
      pollingIntervalRef.current = window.setInterval(async () => {
        if (inFlightPollRef.current) {
          return;
        }

        inFlightPollRef.current = true;
        attempts += 1;

        try {
          const res = await fetch(
            `${chatDbServerUrl}/payment/status?order_id=${encodeURIComponent(orderId)}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          const payload = await res.json().catch(() => ({}));

          if (res.status === 404) {
            return;
          }

          if (!res.ok) {
            throw new Error(payload?.detail || payload?.error || `HTTP ${res.status}`);
          }

          const resolved = resolveUiProjection(payload, trialProjectionRef.current);
          if (resolved.trialProjection) {
            trialProjectionRef.current = resolved.trialProjection;
            setTrialProjection(resolved.trialProjection);
          }
          const mappedStatus = resolved.status;

          if (mappedStatus === "active" || mappedStatus === "trial_active" || mappedStatus === "trial_ending_soon") {
            stopStatusPolling();
            setRuntimeStatus(mappedStatus);
            setActivationNotice(buildStatusNotice(mappedStatus, resolved.trialProjection));
            updateStatusInUrl(mappedStatus, orderId);
            return;
          }

          if (mappedStatus === "manual_review") {
            stopStatusPolling();
            setRuntimeStatus("manual_review");
            setActivationNotice(
              "Payment was received and is under review. Access will activate after manual verification."
            );
            updateStatusInUrl("manual_review", orderId);
            return;
          }

          if (mappedStatus === "activation_failed" || mappedStatus === "trial_activation_failed") {
            stopStatusPolling();
            setRuntimeStatus(mappedStatus);
            setActivationNotice(buildStatusNotice(mappedStatus, resolved.trialProjection));
            updateStatusInUrl(mappedStatus, orderId);
            return;
          }

          if (mappedStatus === "inactive" || mappedStatus === "trial_expired_access_paused") {
            stopStatusPolling();
            setRuntimeStatus(mappedStatus);
            setActivationNotice(buildStatusNotice(mappedStatus, resolved.trialProjection));
            updateStatusInUrl(mappedStatus, orderId);
            return;
          }

          if (mappedStatus === "pending_activation" || mappedStatus === "trial_pending_activation") {
            setRuntimeStatus(mappedStatus);
            setActivationNotice(buildStatusNotice(mappedStatus, resolved.trialProjection));
            updateStatusInUrl(mappedStatus, orderId);
            return;
          }

          if (payload?.status_code && String(payload.status_code) !== "2") {
            stopStatusPolling();
            setRuntimeStatus("activation_failed");
            setActivationNotice(buildStatusNotice("activation_failed", trialProjectionRef.current));
            updateStatusInUrl("activation_failed", orderId);
            return;
          }
        } catch (err) {
          console.error("Subscription status poll failed:", err);
          if (attempts >= STATUS_POLL_MAX_ATTEMPTS) {
            stopStatusPolling();
            setRuntimeStatus("pending_activation");
            setActivationNotice(
              "Payment is still being confirmed. Please refresh in a moment."
            );
            updateStatusInUrl("pending_activation", orderId);
          }
        } finally {
          inFlightPollRef.current = false;
        }

        if (attempts >= STATUS_POLL_MAX_ATTEMPTS) {
          stopStatusPolling();
          setRuntimeStatus("pending_activation");
          setActivationNotice(
            "Activation is taking longer than expected. We will keep your payment and complete access shortly."
          );
          updateStatusInUrl("pending_activation", orderId);
        }
      }, STATUS_POLL_INTERVAL_MS);
    },
    [stopStatusPolling, updateStatusInUrl]
  );

  const startEntitlementPolling = useCallback(
    (accessToken, pendingStatus = "pending_activation") => {
      if (!accessToken) {
        return;
      }

      stopStatusPolling();
      setIsPollingStatus(true);
      setRuntimeStatus(pendingStatus);
      setActivationNotice(buildStatusNotice(pendingStatus, trialProjectionRef.current));
      updateStatusInUrl(pendingStatus);

      let attempts = 0;
      pollingIntervalRef.current = window.setInterval(async () => {
        if (inFlightPollRef.current) {
          return;
        }

        inFlightPollRef.current = true;
        attempts += 1;

        try {
          const res = await fetch(`${chatDbServerUrl}/chat-entitlements/me`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
          const payload = await res.json().catch(() => ({}));

          if (!res.ok) {
            throw new Error(payload?.detail || payload?.error || `HTTP ${res.status}`);
          }

          const resolved = resolveUiProjection(payload, trialProjectionRef.current);
          if (resolved.trialProjection) {
            trialProjectionRef.current = resolved.trialProjection;
            setTrialProjection(resolved.trialProjection);
          }

          const mappedStatus = resolved.status;
          if (
            mappedStatus === "active" ||
            mappedStatus === "trial_active" ||
            mappedStatus === "trial_ending_soon" ||
            mappedStatus === "manual_review" ||
            mappedStatus === "activation_failed" ||
            mappedStatus === "trial_activation_failed" ||
            mappedStatus === "inactive" ||
            mappedStatus === "trial_expired_access_paused"
          ) {
            stopStatusPolling();
            setRuntimeStatus(mappedStatus);
            setActivationNotice(buildStatusNotice(mappedStatus, resolved.trialProjection));
            updateStatusInUrl(mappedStatus);
            return;
          }

          if (mappedStatus === "pending_activation" || mappedStatus === "trial_pending_activation") {
            setRuntimeStatus(mappedStatus);
            setActivationNotice(buildStatusNotice(mappedStatus, resolved.trialProjection));
            updateStatusInUrl(mappedStatus);
            return;
          }
        } catch (err) {
          console.error("Entitlement status poll failed:", err);
          if (attempts >= STATUS_POLL_MAX_ATTEMPTS) {
            stopStatusPolling();
            setRuntimeStatus(pendingStatus);
            setActivationNotice(
              "Access activation is taking longer than expected. Please refresh in a moment."
            );
            updateStatusInUrl(pendingStatus);
          }
        } finally {
          inFlightPollRef.current = false;
        }

        if (attempts >= STATUS_POLL_MAX_ATTEMPTS) {
          stopStatusPolling();
          setRuntimeStatus(pendingStatus);
          setActivationNotice(
            "Activation is taking longer than expected. We will complete access shortly."
          );
          updateStatusInUrl(pendingStatus);
        }
      }, STATUS_POLL_INTERVAL_MS);
    },
    [stopStatusPolling, updateStatusInUrl]
  );

  const triggerStatusPolling = useCallback(
    async (orderId) => {
      try {
        const token = await getAccessToken();
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        startStatusPolling(token, orderId);
      } catch (err) {
        console.error("Failed to start status polling:", err);
        setActivationNotice(
          "Payment completed. We could not auto-refresh access status. Please refresh this page."
        );
      }
    },
    [getAccessToken, startStatusPolling]
  );

  const triggerEntitlementPolling = useCallback(
    async (pendingStatus = "pending_activation") => {
      try {
        const token = await getAccessToken();
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        startEntitlementPolling(token, pendingStatus);
      } catch (err) {
        console.error("Failed to start entitlement polling:", err);
        setActivationNotice(
          "Activation is in progress. We could not auto-refresh access status. Please refresh this page."
        );
      }
    },
    [getAccessToken, startEntitlementPolling]
  );

  const startPaymentWithCallbacks = (paymentPayload, orderId) => {
    payhere.onCompleted = function onCompleted() {
      setTrialProjection((previous) =>
        previous
          ? {
              ...previous,
              trialStatus: "converted_to_paid",
              accessActionStatus: null,
            }
          : previous
      );
      setIsProcessing(false);
      setRuntimeStatus("pending_activation");
      updateStatusInUrl("pending_activation", orderId);
      triggerStatusPolling(orderId);
    };

    payhere.onDismissed = function onDismissed() {
      setIsProcessing(false);
    };

    payhere.onError = function onError(error) {
      console.error("PayHere payment error:", error);
      setIsProcessing(false);
      setRuntimeStatus("inactive");
      updateStatusInUrl("inactive", orderId);
      setErrorMessage("Payment failed. Please try again.");
      setModalState("payment_error");
    };

    payhere.startPayment(paymentPayload);
  };

  const requestPaymentHash = async (accessToken, orderId, amount, currency) => {
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    };

    const res = await fetch(`${paymentsServerUrl}/payment/start`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        order_id: orderId,
        amount,
        currency,
      }),
    });

    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const message = body.error || body.detail || `HTTP ${res.status}`;
      const err = new Error(message);
      err.status = res.status;
      throw err;
    }

    if (body?.order_id_used && body.order_id_used !== orderId) {
      throw new Error("Payment gateway returned mismatched order identity.");
    }

    return body;
  };

  const handleStartTrial = async () => {
    if (isStartingTrial || isProcessing || isPollingStatus) {
      return;
    }

    if (!state?.isAuthenticated) {
      setModalState("auth_required");
      return;
    }

    setIsStartingTrial(true);
    setErrorMessage("");
    setActivationNotice("");

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error("Authentication token is missing.");
      }

      const trialResponse = await fetch(`${chatDbServerUrl}/chat-trials/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ plan_code: TRIAL_PLAN_CODE }),
      });

      const trialPayload = await trialResponse.json().catch(() => ({}));
      if (!trialResponse.ok) {
        throw new Error(trialPayload?.detail || "Failed to start free trial.");
      }

      const resolved = resolveUiProjection(trialPayload, trialProjection);
      if (resolved.trialProjection) {
        trialProjectionRef.current = resolved.trialProjection;
        setTrialProjection(resolved.trialProjection);
      }
      setRuntimeStatus(resolved.status);
      updateStatusInUrl(resolved.status);
      setActivationNotice(
        String(trialPayload?.message || "").trim() ||
          buildStatusNotice(resolved.status, resolved.trialProjection)
      );
      if (resolved.status === "pending_activation" || resolved.status === "trial_pending_activation") {
        startEntitlementPolling(accessToken, resolved.status);
      }
    } catch (error) {
      console.error("Trial start error:", error);
      setErrorMessage(error.message || "Failed to start free trial.");
      setModalState("trial_error");
    } finally {
      setIsStartingTrial(false);
    }
  };

  const handleStartSubscription = async () => {
    if (isProcessing || isStartingTrial || isPollingStatus || isPaidActiveState) {
      return;
    }

    if (!state?.isAuthenticated) {
      setModalState("auth_required");
      return;
    }

    if (typeof payhere === "undefined") {
      setErrorMessage("Payment script is still loading. Please try again in a moment.");
      setModalState("payment_error");
      return;
    }

    setIsProcessing(true);
    setErrorMessage("");
    setActivationNotice("");

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error("Authentication token is missing.");
      }

      const [basicUserInfo, decodedToken] = await Promise.all([
        getBasicUserInfo(),
        getDecodedIDToken(),
      ]);

      const email = basicUserInfo?.email || decodedToken?.email;
      if (!email) {
        throw new Error("Unable to resolve user email from your account.");
      }

      const entitlementResponse = await fetch(
        `${chatDbServerUrl}/chat-entitlements/me`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const entitlementData = await entitlementResponse.json().catch(() => ({}));
      const entitlementResolved = resolveUiProjection(entitlementData, trialProjection);
      const entitlementStatus = entitlementResolved.status;
      if (entitlementResolved.trialProjection) {
        trialProjectionRef.current = entitlementResolved.trialProjection;
        setTrialProjection(entitlementResolved.trialProjection);
      }
      if (entitlementResponse.ok && entitlementStatus === "active") {
        setIsProcessing(false);
        setRuntimeStatus("active");
        updateStatusInUrl("active");
        setActivationNotice("This account already has active chat access.");
        return;
      }

      const checkoutResponse = await fetch(
        `${chatDbServerUrl}/chat-subscriptions/checkout/start`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            return_url: buildStatusUrl("pending"),
            cancel_url: buildStatusUrl("failed"),
          }),
        }
      );

      const checkoutData = await checkoutResponse.json().catch(() => ({}));
      if (!checkoutResponse.ok) {
        throw new Error(
          checkoutData?.detail || "Failed to create subscription checkout session."
        );
      }

      const formattedAmount = Number(checkoutData.amount).toFixed(2);
      const orderId = checkoutData.order_id;
      const currency = checkoutData.currency || "LKR";

      const hashData = await requestPaymentHash(
        accessToken,
        orderId,
        formattedAmount,
        currency
      );

      if (!hashData?.hash || !hashData?.merchant_id) {
        throw new Error("Payment hash generation failed.");
      }

      if (!NOTIFY_URL) {
        throw new Error("Payment notify URL is not configured.");
      }

      const paymentPayload = {
        sandbox: import.meta.env.VITE_SANDBOX !== "false",
        merchant_id: hashData.merchant_id,
        return_url: buildStatusUrl("pending"),
        cancel_url: buildStatusUrl("failed"),
        notify_url: NOTIFY_URL,
        order_id: orderId,
        items: `${checkoutData.plan_name || "Chat Pro"} Subscription`,
        amount: formattedAmount,
        currency,
        first_name: basicUserInfo?.givenName || decodedToken?.given_name || "",
        last_name: basicUserInfo?.familyName || decodedToken?.family_name || "",
        email,
        phone: "",
        address: "",
        city: "",
        country: "Sri Lanka",
        hash: hashData.hash,
        custom_1: "individual",
        custom_2: checkoutData.plan_code || "CHAT_PRO_MONTHLY",
      };

      startPaymentWithCallbacks(paymentPayload, paymentPayload.order_id);
    } catch (error) {
      console.error("Subscription checkout error:", error);
      setErrorMessage(error.message || "Failed to initialize payment.");
      setModalState("payment_error");
      setIsProcessing(false);
    }
  };

  const handleLogin = () => {
    sessionStorage.setItem(
      "redirectAfterLogin",
      `${window.location.pathname}${window.location.search}`
    );
    signIn();
  };

  useEffect(() => {
    let cancelled = false;

    const checkEntitlementOnLoad = async () => {
      if (!state?.isAuthenticated) {
        setIsCheckingEntitlement(false);
        return;
      }

      setIsCheckingEntitlement(true);
      try {
        const accessToken = await getAccessToken();
        if (!accessToken || cancelled) {
          return;
        }

        const res = await fetch(`${chatDbServerUrl}/chat-entitlements/me`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        const payload = await res.json().catch(() => ({}));

        if (cancelled || !res.ok) {
          return;
        }

        const resolved = resolveUiProjection(payload, trialProjection);
        if (resolved.trialProjection) {
          trialProjectionRef.current = resolved.trialProjection;
          setTrialProjection(resolved.trialProjection);
        }

        setRuntimeStatus(resolved.status);
        if (resolved.status === "active") {
          stopStatusPolling();
          updateStatusInUrl("active");
        }
        setActivationNotice(buildStatusNotice(resolved.status, resolved.trialProjection));
      } catch (error) {
        console.error("Initial entitlement check failed:", error);
      } finally {
        if (!cancelled) {
          setIsCheckingEntitlement(false);
        }
      }
    };

    checkEntitlementOnLoad();

    return () => {
      cancelled = true;
    };
  }, [state?.isAuthenticated, getAccessToken, stopStatusPolling, updateStatusInUrl]);

  useEffect(() => {
    return () => {
      stopStatusPolling();
    };
  }, [stopStatusPolling]);

  useEffect(() => {
    if (!state?.isAuthenticated) {
      return;
    }

    if (statusFromQuery !== "pending_activation" && statusFromQuery !== "trial_pending_activation") {
      return;
    }

    if (orderIdFromQuery) {
      triggerStatusPolling(orderIdFromQuery);
      return;
    }

    triggerEntitlementPolling(statusFromQuery);
  }, [
    state?.isAuthenticated,
    orderIdFromQuery,
    statusFromQuery,
    triggerStatusPolling,
    triggerEntitlementPolling,
  ]);

  const trialStatus = normalizeTrialLifecycleStatus(trialProjection?.trialStatus);
  const trialEligibleFromBackend = trialProjection?.backendEligible;
  const isAuthenticated = Boolean(state?.isAuthenticated);
  const trialAlreadyUsed =
    trialProjection?.accessActionStatus === "trial_already_used" ||
    trialStatus === "converted_to_paid" ||
    trialStatus === "expired" ||
    trialStatus === "access_removal_started" ||
    status === "trial_expired_access_paused";
  const isTrialActiveState = status === "trial_active";
  const isTrialEndingSoonState = status === "trial_ending_soon";
  const isTrialActiveOrEndingState = isTrialActiveState || isTrialEndingSoonState;
  const isTrialExpiredState = status === "trial_expired_access_paused";
  const isPendingActivationState =
    status === "pending_activation" || status === "trial_pending_activation";
  const isManualReviewState = status === "manual_review";
  const isActivationFailedState =
    status === "activation_failed" || status === "trial_activation_failed";
  const trialIneligibleFromBackend = trialEligibleFromBackend === false && !isPaidActiveState;
  const isTrialExpiredOrUsedState =
    isTrialExpiredState || trialAlreadyUsed || trialIneligibleFromBackend;
  const isTrialEligibleState =
    isAuthenticated &&
    trialEligibleFromBackend === true &&
    !isPaidActiveState &&
    !isPendingActivationState &&
    !isManualReviewState &&
    !isActivationFailedState &&
    !trialAlreadyUsed &&
    !isTrialActiveOrEndingState;
  const trialPrimaryMessage = isTrialEndingSoonState
    ? "Keep access after trial. Subscribe before your trial ends."
    : "Subscribe now to continue without interruption after your trial ends.";
  const isBusy = isCheckingEntitlement || isProcessing || isStartingTrial || isPollingStatus;
  const trialActionDisabled = isBusy;
  const subscriptionActionDisabled = isBusy || isPaidActiveState;

  let planPrimaryLabel = "Start Subscription";
  let planPrimaryDisabled = subscriptionActionDisabled;
  let planPrimaryAction = handleStartSubscription;
  let planPrimaryHref = null;
  let planPrimaryAriaLabel = "Start monthly chat subscription";
  let planHelperText = activationNotice || "";
  const showAccountDetailsLink =
    isAuthenticated &&
    (
      isPaidActiveState ||
      isPendingActivationState ||
      isManualReviewState ||
      isActivationFailedState ||
      isTrialActiveOrEndingState ||
      isTrialExpiredOrUsedState
    );

  if (!isAuthenticated) {
    planPrimaryLabel = "Start 14-Day Free Trial";
    planPrimaryDisabled = trialActionDisabled;
    planPrimaryAction = handleStartTrial;
    planPrimaryAriaLabel = "Start 14-day free trial";
    planHelperText = "Login required to start trial or subscribe.";
  } else if (isPaidActiveState) {
    planPrimaryLabel = "Open Chat";
    planPrimaryDisabled = false;
    planPrimaryAction = null;
    planPrimaryHref = chatUrl;
    planPrimaryAriaLabel = "Open chat workspace";
    planHelperText = "Your subscription is active.";
  } else if (isPendingActivationState) {
    planPrimaryLabel = "Activating Access...";
    planPrimaryDisabled = true;
    planPrimaryAction = null;
    planPrimaryAriaLabel = "Access activation in progress";
    planHelperText = "Access activation is in progress.";
  } else if (isManualReviewState) {
    planPrimaryLabel = "Under Review";
    planPrimaryDisabled = true;
    planPrimaryAction = null;
    planPrimaryAriaLabel = "Payment under review";
    planHelperText = "Payment is under review. Access will activate after verification.";
  } else if (isActivationFailedState) {
    planPrimaryLabel = isProcessing ? "Processing..." : "Start Subscription";
    planPrimaryDisabled = subscriptionActionDisabled;
    planPrimaryAction = handleStartSubscription;
    planPrimaryAriaLabel = "Start monthly chat subscription";
    planHelperText =
      "Activation failed. Please retry or contact support if the issue continues.";
  } else if (isTrialActiveOrEndingState) {
    planPrimaryLabel = isProcessing ? "Processing..." : "Subscribe Now";
    planPrimaryDisabled = subscriptionActionDisabled;
    planPrimaryAction = handleStartSubscription;
    planPrimaryAriaLabel = "Subscribe now";
    planHelperText = trialPrimaryMessage;
  } else if (isTrialExpiredOrUsedState) {
    planPrimaryLabel = isProcessing ? "Processing..." : "Start Subscription";
    planPrimaryDisabled = subscriptionActionDisabled;
    planPrimaryAction = handleStartSubscription;
    planPrimaryAriaLabel = "Start monthly chat subscription";
    planHelperText = "Free trial already used for this account.";
  } else if (isTrialEligibleState) {
    planPrimaryLabel = isStartingTrial ? "Starting Trial..." : "Start 14-Day Free Trial";
    planPrimaryDisabled = trialActionDisabled;
    planPrimaryAction = handleStartTrial;
    planPrimaryAriaLabel = "Start 14-day free trial";
    planHelperText = "No card required. One free trial per account.";
  } else if (isCheckingEntitlement) {
    planPrimaryLabel = "Checking Access...";
    planPrimaryDisabled = true;
    planPrimaryAction = null;
    planPrimaryAriaLabel = "Checking account access";
    planHelperText = "Checking your current access status.";
  }

  const modalConfig =
    modalState === "auth_required"
      ? {
          title: "Login required",
          description: "Please log in to start your free trial or manage your subscription.",
          actions: [
            { label: "Close", onClick: () => setModalState(null) },
            { label: "Log in", onClick: handleLogin, variant: "primary" },
          ],
        }
      : modalState === "payment_error"
      ? {
          title: "Payment initialization failed",
          description: errorMessage || "Unable to start payment. Please try again.",
          actions: [
            { label: "Close", onClick: () => setModalState(null), variant: "primary" },
          ],
        }
      : modalState === "trial_error"
      ? {
          title: "Trial start failed",
          description: errorMessage || "Unable to start trial. Please try again.",
          actions: [
            { label: "Close", onClick: () => setModalState(null), variant: "primary" },
          ],
        }
      : null;

  return (
    <MainLayout>
      <Helmet>
        <title>Chat | paralegal.lk</title>
        <meta
          name="description"
          content="Start a free trial of Paralegal’s dedicated chat workspace for AI-assisted legal research, drafting, and analysis."
        />
        <link rel="canonical" href="https://www.paralegal.lk/chat" />
      </Helmet>

      <article className="chat-pricing-page w-full max-w-[1080px] mx-auto px-6 md:px-12 py-10 md:py-16 text-pl-text-primary">
        {/* Masthead row */}
        <header className="flex flex-wrap justify-between items-baseline gap-y-2 pb-4 mb-10 md:mb-14 border-b border-pl-border font-mono text-10 md:text-11 uppercase tracking-[0.22em] text-pl-text-tertiary">
          <div className="flex items-baseline gap-3">
            <span className="text-pl-accent">“</span>
            <span>{chatDomainLabel}</span>
          </div>
          <span>Subscription workspace</span>
        </header>

        <ChatStatusBanner status={status} />

        {/* Hero */}
        <div className="mb-12 md:mb-16">
          <h1 className="font-display leading-[1.02] tracking-[-0.015em] text-[42px] sm:text-[56px] md:text-[74px] lg:text-[82px] mb-6 reveal r-1">
            AI legal research and drafting
            <br />
            for Sri Lankan <em className="italic text-pl-accent">practice</em>.
          </h1>
          <p className="font-display text-18 md:text-21 leading-[1.52] text-pl-text-secondary max-w-[46ch] mb-8 reveal r-2">
            <a
              href={chatUrl}
              className="text-pl-accent underline underline-offset-4 hover:text-pl-accent-hover"
            >
              {chatDomainLabel}
            </a>{" "}
            is our subscription workspace for legal research, drafting,
            and analysis. Work from the first question to a written output in
            one environment.
          </p>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 reveal r-3">
            {canOpenChat && (
              <a
                href={chatUrl}
                className="group inline-flex items-center gap-3 px-7 py-4 border border-pl-border rounded-pl font-mono text-11 md:text-12 uppercase tracking-[0.2em] text-pl-text-primary hover:border-pl-accent hover:text-pl-accent transition-colors duration-200"
              >
                Open Chat
                <span className="transition-transform duration-200 group-hover:translate-x-1">
                  →
                </span>
              </a>
            )}
            <Link
              to="/about"
              className="font-mono text-10 md:text-11 uppercase tracking-[0.18em] text-pl-text-tertiary hover:text-pl-accent transition-colors duration-200"
            >
              Learn about Paralegal →
            </Link>
          </div>
        </div>

        {/* § I — The plan (conversion core) */}
        <SectionLabel n="I" label="The plan" />

        <div className="reveal r-4 mb-20 md:mb-28 rounded-pl-lg border border-pl-border bg-pl-bg-elevated shadow-pl-md overflow-hidden">
          <div className="flex flex-wrap justify-between items-baseline gap-2 px-6 md:px-8 py-4 border-b border-pl-border font-mono text-10 md:text-11 uppercase tracking-[0.2em] text-pl-text-tertiary">
            <span className="text-pl-accent">Chat Pro · Monthly</span>
            <span>One tier, individual or team</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_minmax(0,360px)]">
            {/* Price + what's included */}
            <div className="p-6 md:p-8 border-b md:border-b-0 md:border-r border-pl-border">
              <div className="flex items-end gap-3 mb-6">
                <span className="font-display text-[44px] md:text-[56px] leading-none tracking-[-0.02em]">
                  Rs. 20,000
                </span>
                <span className="pb-2 font-mono text-11 uppercase tracking-[0.18em] text-pl-text-tertiary">
                  / month
                </span>
              </div>
              <p className="text-15 md:text-16 leading-[1.7] text-pl-text-secondary max-w-[48ch] mb-7">
                One subscription tier for legal professionals and legal teams.
              </p>

              <p className="font-mono text-10 uppercase tracking-[0.2em] text-pl-text-tertiary mb-4">
                What’s included
              </p>
              <ul className="space-y-3">
                {INCLUDED_ITEMS.map((item) => (
                  <li
                    key={item}
                    className="flex gap-3 text-15 leading-[1.5] text-pl-text-primary"
                  >
                    <span className="text-pl-accent pt-[2px]" aria-hidden="true">
                      ✓
                    </span>
                    <span>
                      {item === "Monthly access to chat.paralegal.lk" ? (
                        <>
                          Monthly access to{" "}
                          <a
                            href={chatUrl}
                            className="text-pl-accent underline underline-offset-4 hover:text-pl-accent-hover"
                          >
                            {chatDomainLabel}
                          </a>
                        </>
                      ) : (
                        item
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Action panel */}
            <div className="p-6 md:p-8 bg-pl-bg-subtle flex flex-col">
              {planPrimaryHref ? (
                <a
                  href={planPrimaryHref}
                  className="group inline-flex w-full items-center justify-center gap-3 px-7 py-4 bg-pl-accent text-white rounded-pl font-mono text-11 md:text-12 uppercase tracking-[0.2em] hover:bg-pl-accent-hover transition-colors duration-200"
                >
                  {planPrimaryLabel}
                  <span className="transition-transform duration-200 group-hover:translate-x-1">
                    →
                  </span>
                </a>
              ) : (
                <button
                  type="button"
                  onClick={planPrimaryAction || undefined}
                  disabled={planPrimaryDisabled}
                  aria-label={planPrimaryAriaLabel}
                  className={`group inline-flex w-full items-center justify-center gap-3 px-7 py-4 rounded-pl font-mono text-11 md:text-12 uppercase tracking-[0.2em] text-white transition-colors duration-200 ${
                    planPrimaryDisabled
                      ? "cursor-not-allowed bg-pl-text-tertiary"
                      : "bg-pl-accent hover:bg-pl-accent-hover"
                  }`}
                >
                  {planPrimaryLabel}
                  {!planPrimaryDisabled && (
                    <span className="transition-transform duration-200 group-hover:translate-x-1">
                      →
                    </span>
                  )}
                </button>
              )}

              {planHelperText && (
                <p className="mt-4 text-14 leading-[1.6] text-pl-text-secondary">
                  {planHelperText}
                </p>
              )}
              {showAccountDetailsLink && (
                <Link
                  to="/my-account"
                  className="mt-3 inline-flex font-mono text-10 uppercase tracking-[0.18em] text-pl-accent hover:text-pl-accent-hover transition-colors duration-200"
                >
                  Subscription details →
                </Link>
              )}

              <div className="mt-auto pt-6">
                <p className="text-13 leading-[1.6] text-pl-text-tertiary">
                  Checkout is handled through PayHere. Access is enabled after
                  payment confirmation.
                </p>
                <p className="mt-3 text-12 leading-[1.6] text-pl-text-tertiary">
                  By continuing, you agree to our{" "}
                  <Link to="/terms" className="text-pl-accent hover:underline">
                    Terms
                  </Link>
                  ,{" "}
                  <Link to="/privacy" className="text-pl-accent hover:underline">
                    Privacy Policy
                  </Link>
                  , and{" "}
                  <Link to="/refund" className="text-pl-accent hover:underline">
                    Refund Policy
                  </Link>
                  .
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* § II — What it does */}
        <SectionLabel n="II" label="What it does" />

        <div className="space-y-14 md:space-y-20 mb-20 md:mb-28">
          {PRODUCT_FEATURES.map((feature, index) => (
            <FeatureArticle
              key={feature.n}
              {...feature}
              revealClass={`cap-reveal cr-${index + 1}`}
            />
          ))}
        </div>

        {/* § III — Firms and teams */}
        <SectionLabel n="III" label="Firms and teams" />

        <div className="rounded-pl-lg border border-pl-border bg-pl-bg-elevated p-6 md:p-8">
          <h2 className="font-display text-28 md:text-34 leading-[1.15] text-pl-text-primary mb-4">
            Access for your firm or team.
          </h2>
          <p className="text-15 md:text-16 leading-[1.75] text-pl-text-secondary max-w-[60ch] mb-6">
            For chambers, law firms, and legal teams that need multi-user
            access, shared onboarding, and tailored support, choose a group
            subscription. Per-seat pricing falls as the team grows.
          </p>

          <ul className="space-y-3 mb-8 max-w-[60ch]">
            {GROUP_PLAN_HIGHLIGHTS.map((item) => (
              <li
                key={item}
                className="flex gap-3 text-15 leading-[1.5] text-pl-text-primary"
              >
                <span className="text-pl-accent pt-[2px]" aria-hidden="true">
                  ✓
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <a
            href="mailto:admin@paralegal.lk?subject=Group%20Subscription%20Inquiry"
            className="group inline-flex items-center gap-3 px-7 py-4 bg-pl-text-primary text-pl-bg rounded-pl font-mono text-11 md:text-12 uppercase tracking-[0.2em] hover:bg-pl-accent transition-colors duration-200"
          >
            Contact us
            <span className="transition-transform duration-200 group-hover:translate-x-1">
              →
            </span>
          </a>
        </div>

        {/* Page-scoped styles */}
        <style>{`
          .chat-pricing-page .reveal { opacity: 0; transform: translateY(14px); animation: cp-revealUp .85s cubic-bezier(.2,.7,.2,1) forwards; }
          .chat-pricing-page .r-1 { animation-delay: .04s; }
          .chat-pricing-page .r-2 { animation-delay: .14s; }
          .chat-pricing-page .r-3 { animation-delay: .24s; }
          .chat-pricing-page .r-4 { animation-delay: .34s; }
          @keyframes cp-revealUp { to { opacity: 1; transform: translateY(0); } }

          .chat-pricing-page .cap-reveal { opacity: 0; transform: translateY(14px); animation: cp-capIn .85s cubic-bezier(.2,.7,.2,1) forwards; }
          .chat-pricing-page .cr-1 { animation-delay: .10s; }
          .chat-pricing-page .cr-2 { animation-delay: .18s; }
          .chat-pricing-page .cr-3 { animation-delay: .26s; }
          .chat-pricing-page .cr-4 { animation-delay: .34s; }
          .chat-pricing-page .cr-5 { animation-delay: .42s; }
          @keyframes cp-capIn { to { opacity: 1; transform: translateY(0); } }

          @media (prefers-reduced-motion: reduce) {
            .chat-pricing-page .reveal,
            .chat-pricing-page .cap-reveal { animation: none; opacity: 1; transform: none; }
          }
        `}</style>
      </article>

      <DialogModal
        isOpen={Boolean(modalConfig)}
        onClose={() => setModalState(null)}
        title={modalConfig?.title}
        description={modalConfig?.description}
        actions={modalConfig?.actions}
      />
    </MainLayout>
  );
};

const SectionLabel = ({ n, label }) => (
  <div className="relative border-t border-pl-border mt-12 mb-10 md:mb-14">
    <div className="absolute -top-[10px] left-0 bg-pl-bg pr-4 font-mono text-10 md:text-11 uppercase tracking-[0.22em] text-pl-text-tertiary flex items-baseline gap-3">
      <span className="text-pl-accent">§ {n}</span>
      <span>{label}</span>
    </div>
  </div>
);

const FeatureArticle = ({ n, tag, title, description, revealClass }) => (
  <section
    className={`grid grid-cols-1 md:grid-cols-[140px_1fr] gap-y-3 gap-x-10 md:gap-x-16 items-start ${revealClass}`}
  >
    <div className="font-mono uppercase tracking-[0.22em] pt-1 flex md:block items-baseline gap-3">
      <span className="text-11 text-pl-accent">¶ {n}</span>
      <span className="block text-pl-text-tertiary text-10 md:mt-2">{tag}</span>
    </div>
    <div>
      <h3 className="font-display text-24 md:text-32 leading-[1.18] text-pl-text-primary mb-4 max-w-[28ch] tracking-[-0.005em]">
        {title}
      </h3>
      <p className="text-15 md:text-16 leading-[1.78] text-pl-text-secondary max-w-[58ch]">
        {description}
      </p>
    </div>
  </section>
);

export default ChatPricing;
